import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Play, Sparkles } from "lucide-react";
import { useSite, wa, ICONS } from "@/context/ContentContext";
import { renderAccent } from "@/components/RichText";
import { MaskedLines, Reveal, FadeIn } from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import Marquee from "@/components/Marquee";
import RoomCard from "@/components/RoomCard";
import CTABanner from "@/components/CTABanner";
import { UltraVipShowcase, LocationAdvantages } from "@/components/HomeExtras";
import VideoModal from "@/components/VideoModal";
import WhatsAppIcon from "@/components/WhatsAppIcon";

const Hero = () => {
  const D = useSite();
  const slides = D.hero.slides;
  const [index, setIndex] = useState(0);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % slides.length), 6500);
    return () => clearInterval(t);
  }, [slides.length]);

  const safeIndex = slides.length ? index % slides.length : 0;

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[620px] overflow-hidden">
      <AnimatePresence>
        {slides.length > 0 && (
          <motion.div
            key={safeIndex}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 1.4, ease: "easeOut" } }}
            exit={{ opacity: 0, transition: { duration: 1.2, ease: "easeIn" } }}
          >
            {slides[safeIndex].src ? (
              <motion.img
                src={slides[safeIndex].src}
                alt={slides[safeIndex].alt}
                className="h-full w-full object-cover"
                initial={{ scale: 1.14 }}
                animate={{ scale: 1.02 }}
                transition={{ duration: 7, ease: "linear" }}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-charcoal-800 via-charcoal-900 to-charcoal-950">
                <span className="select-none font-serif text-6xl italic text-gold/20 md:text-8xl">
                  FOTO {safeIndex + 1}
                </span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-b from-charcoal-950/55 via-charcoal-950/25 to-charcoal-950/70" />
      <div className="absolute inset-0 bg-gradient-to-r from-charcoal-950/40 to-transparent" />

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-6 pb-28 md:justify-center md:px-12 md:pb-0"
      >
        <FadeIn delay={0.25}>
          <span className="inline-flex items-center gap-3 font-accent text-[11px] uppercase tracking-[0.35em] text-gold-light">
            <span className="h-px w-10 bg-gold-light/70" />
            {D.hero.overline}
          </span>
        </FadeIn>
        <MaskedLines
          as="h1"
          delay={0.4}
          lines={D.hero.titleLines.map((l) => renderAccent(l, "italic text-gold-light"))}
          className="mt-7 font-serif text-[2.9rem] leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-7xl xl:text-[5.4rem]"
        />
        <FadeIn delay={1.0}>
          <p className="mt-7 max-w-xl text-base leading-relaxed text-white/75 md:text-lg">
            {D.hero.subtitle}
          </p>
        </FadeIn>
        <FadeIn delay={1.15} className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
          <Link
            data-testid="hero-discover-cta"
            to="/odalar"
            className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-white px-8 py-4 text-sm font-semibold text-charcoal-900 transition-all duration-500 ease-premium hover:-translate-y-0.5 hover:shadow-lift"
          >
            {D.hero.primaryCta}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <a
            data-testid="hero-whatsapp-cta"
            href={wa(D.site)}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center justify-center gap-2.5 rounded-full border border-white/35 bg-white/10 px-8 py-4 text-sm font-semibold text-white backdrop-blur-md transition-all duration-500 ease-premium hover:border-white/60 hover:bg-white/20"
          >
            <WhatsAppIcon className="h-4 w-4" />
            {D.hero.secondaryCta}
          </a>
        </FadeIn>
      </motion.div>

      <div className="absolute bottom-10 left-6 z-10 flex items-center gap-3 md:left-12">
        {slides.map((_, i) => (
          <button
            key={i}
            data-testid={`hero-dot-${i}`}
            onClick={() => setIndex(i)}
            aria-label={`Slayt ${i + 1}`}
            className="group py-2"
          >
            <span
              className={`block h-[2px] rounded-full transition-all duration-700 ease-premium ${
                i === safeIndex ? "w-12 bg-gold-light" : "w-6 bg-white/35 group-hover:bg-white/70"
              }`}
            />
          </button>
        ))}
      </div>
      <div className="absolute bottom-10 right-6 z-10 hidden font-accent text-xs tracking-[0.3em] text-white/60 md:right-12 md:block">
        {String(safeIndex + 1).padStart(2, "0")} <span className="text-gold-light/70">/</span>{" "}
        {String(slides.length).padStart(2, "0")}
      </div>
      <div className="absolute bottom-0 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-3 pb-6 md:flex">
        <span className="font-accent text-[10px] uppercase tracking-[0.4em] text-white/45">Keşfet</span>
        <div className="relative h-12 w-px overflow-hidden bg-white/20">
          <motion.span
            className="absolute left-0 top-0 h-5 w-px bg-gold-light"
            animate={{ y: [-20, 48] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </div>
    </section>
  );
};

const IntroSection = () => {
  const D = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-50, 50]);

  return (
    <section ref={ref} className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36">
      <div className="grid items-center gap-14 md:grid-cols-12 md:gap-10">
        <div className="md:col-span-6 lg:col-span-5">
          <SectionHeading
            overline={D.intro.overline}
            title={D.intro.title}
            description={D.intro.description}
          />
          <Reveal delay={0.25}>
            <Link
              data-testid="intro-about-link"
              to="/hakkimizda"
              className="group mt-9 inline-flex items-center gap-2 text-sm font-semibold text-charcoal-900"
            >
              <span className="border-b border-gold/60 pb-1 transition-colors duration-300 group-hover:border-gold-dark group-hover:text-gold-dark">
                {D.intro.linkText}
              </span>
              <ArrowUpRight className="h-4 w-4 text-gold-dark transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </Reveal>
        </div>
        <div className="relative md:col-span-6 lg:col-span-7">
          <Reveal delay={0.15}>
            <div className="relative overflow-hidden rounded-[2rem] shadow-lift">
              <motion.img
                style={{ y: imgY }}
                src={D.intro.image}
                alt="Ada yurdu ortak yaşam alanı"
                loading="lazy"
                className="h-[420px] w-full scale-110 object-cover md:h-[540px]"
              />
            </div>
          </Reveal>
          <Reveal delay={0.35} y={24} className="absolute -bottom-8 left-6 md:-left-10 md:bottom-12">
            <div className="rounded-3xl border border-white/40 bg-white/80 px-7 py-6 shadow-lift backdrop-blur-xl">
              <p className="font-serif text-3xl text-charcoal-900">{D.intro.badgeValue}</p>
              <p className="mt-1 font-accent text-[11px] uppercase tracking-[0.25em] text-charcoal-900/55">
                {D.intro.badgeLabel}
              </p>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="mt-28 grid grid-cols-2 gap-x-6 gap-y-12 lg:grid-cols-4">
        {D.stats.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.1}>
            <div data-testid={`stat-${i}`} className="border-l border-gold/40 pl-6">
              <p className="font-serif text-4xl tracking-tight text-charcoal-900 md:text-5xl">{s.value}</p>
              <p className="mt-2 text-sm text-muted-foreground">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const ManifestoSection = () => {
  const D = useSite();
  return (
    <section className="bg-gradient-to-b from-cream-50 to-cream-100 py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <SectionHeading overline="Manifestomuz" title="Üç ilke, *tek söz*" />
        <div className="mt-16">
          {D.manifesto.map((m, i) => (
            <Reveal key={m.no} delay={i * 0.08}>
              <div
                data-testid={`manifesto-chapter-${m.no}`}
                className="group grid items-baseline gap-3 border-t border-charcoal-900/10 py-10 transition-all duration-500 ease-premium hover:pl-3 md:grid-cols-12 md:gap-6 md:py-12"
              >
                <span className="font-serif text-5xl text-gold/35 transition-colors duration-500 group-hover:text-gold md:col-span-2 md:text-6xl">
                  {m.no}
                </span>
                <h3 className="font-serif text-3xl tracking-tight text-charcoal-900 md:col-span-3 md:text-4xl">
                  {m.title}
                </h3>
                <p className="max-w-xl text-base leading-relaxed text-muted-foreground md:col-span-7">
                  {m.text}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const RoomsPreview = () => {
  const D = useSite();
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
      <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
        <SectionHeading
          overline={D.roomsPreview.overline}
          title={D.roomsPreview.title}
          description={D.roomsPreview.description}
        />
        <Reveal delay={0.2} className="shrink-0">
          <Link
            data-testid="rooms-preview-all-link"
            to="/odalar"
            className="group inline-flex items-center gap-2.5 rounded-full border border-charcoal-900/15 px-7 py-3.5 text-sm font-semibold text-charcoal-900 transition-all duration-500 ease-premium hover:border-charcoal-900 hover:bg-charcoal-900 hover:text-white"
          >
            {D.roomsPreview.allLabel}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-10 md:grid-cols-3 md:gap-8">
        {D.rooms.slice(0, 3).map((room, i) => (
          <RoomCard key={room.slug || room.name} room={room} index={i} />
        ))}
      </div>
    </section>
  );
};

const ServicesPreview = () => {
  const D = useSite();
  return (
    <section className="bg-gradient-to-b from-cream-100 to-rose-50 py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <SectionHeading
          align="center"
          overline={D.servicesPreview.overline}
          title={D.servicesPreview.title}
          description={D.servicesPreview.description}
        />
        <div className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {D.services.map((s, i) => {
            const Icon = ICONS[s.icon] || Sparkles;
            return (
              <Reveal key={s.title} delay={(i % 4) * 0.08} className="h-full">
                <div
                  data-testid={`service-card-${i}`}
                  className="group h-full rounded-3xl border border-charcoal-900/5 bg-white/80 p-7 shadow-premium backdrop-blur transition-all duration-500 ease-premium hover:-translate-y-1.5 hover:border-gold/30 hover:shadow-lift"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gold/10 text-gold-dark transition-colors duration-500 group-hover:bg-gold/20">
                    <Icon
                      className="h-5 w-5 transition-transform duration-500 ease-premium group-hover:-rotate-6 group-hover:scale-110"
                      strokeWidth={1.5}
                    />
                  </div>
                  <h3 className="mt-6 text-base font-semibold text-charcoal-900">{s.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const VideoShowcase = () => {
  const D = useSite();
  const [active, setActive] = useState(null);
  const video = D.videos[0];
  if (!video) return null;
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
      <Reveal>
        <div
          data-testid="video-showcase"
          className="group relative h-[70vh] min-h-[420px] cursor-pointer overflow-hidden rounded-[2.5rem] shadow-lift"
          onClick={() => setActive(video)}
        >
          <img
            src={video.poster}
            alt={video.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-[1600ms] ease-premium group-hover:scale-[1.04]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal-950/70 via-charcoal-950/20 to-charcoal-950/30" />
          <div className="absolute left-8 top-8 md:left-14 md:top-14">
            <p className="font-accent text-[11px] uppercase tracking-[0.35em] text-gold-light">{D.homeVideo.overline}</p>
            <h2 className="mt-4 max-w-md font-serif text-3xl leading-tight tracking-tight text-white sm:text-4xl md:text-5xl">
              {D.homeVideo.title}
            </h2>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <button
              data-testid="video-showcase-play"
              aria-label="Tanıtım filmini oynat"
              className="relative flex h-20 w-20 items-center justify-center rounded-full border border-white/40 bg-white/15 text-white backdrop-blur-md transition-all duration-500 ease-premium group-hover:scale-110 group-hover:bg-white/25 md:h-24 md:w-24"
            >
              <span aria-hidden="true" className="absolute inset-0 rounded-full border border-white/50 animate-pulse-ring" />
              <Play className="ml-1 h-7 w-7 fill-current" />
            </button>
          </div>
          <div className="absolute bottom-8 right-8 font-accent text-xs tracking-[0.3em] text-white/70 md:bottom-12 md:right-14">
            {video.duration}
          </div>
        </div>
      </Reveal>
      <VideoModal video={active} onClose={() => setActive(null)} />
    </section>
  );
};

export default function HomePage() {
  return (
    <>
      <Hero />
      <Marquee />
      <IntroSection />
      <UltraVipShowcase />
      <ManifestoSection />
      <RoomsPreview />
      <ServicesPreview />
      <LocationAdvantages />
      <VideoShowcase />
      <CTABanner />
    </>
  );
}
